import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { HostCfgDTO } from "@dto/hostCfgResponse.dto";
import { MicroserviceVariablesResponse } from "@dto/microserviceVariables.dto";
import { Button, Form, Input, InputRef, Space, Spin, Typography } from "antd";
import axios, { AxiosError } from "axios";
import { Dispatch, useEffect, useRef, useState } from "react";
import { XmModal } from "src/component/modal/XmModal";
import { Log } from "src/service/log.service";
import { deleteApi, postApi, putApi } from "src/util/apiCalls";
import { GLOB } from "src/util/Glob";
import { IFormService } from "../application/ApplicationCfg";
import { API_URL_MICROS } from "../application/Services/components/ServicesTable";
const { Text } = Typography;

interface AdvancedSettingsCFGProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<React.SetStateAction<boolean>> | ((b: boolean) => void);
  record: HostCfgDTO;
}

const AdvancedSettingsCFG = ({ record, isModalOpen, setIsModalOpen }: AdvancedSettingsCFGProps) => {

  const [form] = Form.useForm();
  const [keysOfValues, setKeysOfValues] = useState<string[]>([]);
  const [isLoadingVariables, setIsLoadingVariables] = useState(false);
  const originalItemsRef = useRef<Map<string, { key: string; value: string; }>>(new Map());
  const lastKeyRef = useRef<InputRef>(null);

  const getData = async () => {
    setKeysOfValues([]);
    try {
      setIsLoadingVariables(true);
      const responseVariables = await axios.get<MicroserviceVariablesResponse>(
        API_URL_MICROS.VARIABLES + `?hostcfg_id=${record.hostcfg_id}`
      );
      const { microserviceVariables } = responseVariables.data.data;
      const filtered = microserviceVariables.filter((v) => v.microservice_variables_id != null);
      originalItemsRef.current = new Map(filtered.map((v) => [v.microservice_variables_id, { key: v.key, value: v.value }]));
      form.resetFields();
      form.setFieldsValue({ items: filtered });
    } catch (error) {
      const axiosError = error as AxiosError<BasicResponseDTO>;
      Log.error(axiosError.response?.data?.message ?? axiosError.message ?? "Failed to load variables");
    } finally {
      setIsLoadingVariables(false);
    }
  };

  const onAddVariables = (add: () => void) => {
    add();
    setTimeout(() => lastKeyRef.current?.focus());
  };

  const onDeleteValue = (func: (index: number) => void, index: number) => {
    const id = form.getFieldValue(["items", index, "microservice_variables_id"]);
    func(index);
    if (id != null) {
      setKeysOfValues((prevData) => [...prevData, id]);
    }
  };

  const onFinish = async (values: IFormService) => {
    const { items } = values;

    const existingItems = items.filter((o) => o?.microservice_variables_id != null);
    const existingKeys = new Set(existingItems.map((o) => o.key?.trim()));

    const newItems = items.filter(
      (o) => o && o.microservice_variables_id == null && o.key?.trim() && o.value?.trim() && !existingKeys.has(o.key.trim())
    );

    const newObj = {
      microserviceVariables: newItems.map((obj) => ({ ...obj, hostcfg_id: record.hostcfg_id })),
    };

    setIsLoadingVariables(true);
    const errors: string[] = [];

    if (keysOfValues.length) {
      try {
        await deleteApi(API_URL_MICROS.VARIABLES, { data: { ids: keysOfValues } });
      } catch (e) {
        const err = e as AxiosError<BasicResponseDTO>;
        errors.push(err.response?.data?.message ?? err.message ?? "Failed to delete variables");
      }
    }

    const changedItems = existingItems.filter((it) => {
      const orig = originalItemsRef.current.get(it.microservice_variables_id);
      return orig?.key !== it.key || orig?.value !== it.value;
    });

    if (changedItems.length) {
      const results = await Promise.allSettled(
        changedItems.map((element) =>
          putApi(`${API_URL_MICROS.VARIABLES}/${element.microservice_variables_id}`, element)
        )
      );
      results.forEach((r) => {
        if (r.status === "rejected") {
          const err = r.reason as AxiosError<BasicResponseDTO>;
          errors.push(err.response?.data?.message ?? err.message ?? "Failed to update variable");
        }
      });
    }

    if (newObj.microserviceVariables.length) {
      try {
        await postApi(API_URL_MICROS.VARIABLES, newObj);
      } catch (e) {
        const err = e as AxiosError<BasicResponseDTO>;
        errors.push(err.response?.data?.message ?? err.message ?? "Failed to create variables");
      }
    }

    setIsLoadingVariables(false);

    if (errors.length) {
      Log.error(errors.join("; "));
      void getData();
      return;
    }

    Log.success("Changes successfully implemented");
    setKeysOfValues([]);
    void getData();
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (record && isModalOpen) {
      void getData();
    }
  }, [record, isModalOpen]);

  return (
    <XmModal
      title={record ? `Advanced settings for ${record.label}` : "Advanced settings"}
      width={356}
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      onOk={() => form.submit()}
      mask={XmModal.MASK_NOT_CLOSABLE}
      footer={false}
    >
      <Spin spinning={false}>
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, paddingRight: 22 }}>
            <Text strong style={{ flex: 1 }}>Key</Text>
            <Text strong style={{ flex: 1 }}>Value</Text>
          </div>
          <Form form={form} onFinish={onFinish} autoComplete='off'>
            <Form.List name='items'>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key}>
                      <Form.Item {...restField} name={[name, "microservice_variables_id"]} hidden>
                        <Input />
                      </Form.Item>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                        <Form.Item
                          {...restField}
                          style={{ flex: 1 }}
                          name={[name, "key"]}
                          rules={[
                            { required: true, message: "Missing key" },
                            GLOB.TitleCharactersValidatorRule,
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value?.trim()) return Promise.resolve();
                                const items = getFieldValue("items") || [];
                                const dup = items.some((it, i) => i !== name && it?.key?.trim() === value.trim());
                                return dup ? Promise.reject(new Error("Duplicate key")) : Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input
                            ref={index === fields.length - 1 ? lastKeyRef : undefined}
                            placeholder='key'
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          style={{ flex: 1 }}
                          name={[name, "value"]}
                          rules={[{ required: true, message: "Missing value" }]}
                        >
                          <Input placeholder='value' />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => onDeleteValue(remove, name)} />
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      style={{ width: "calc(100% - 22px)" }}
                      type='dashed'
                      onClick={() => onAddVariables(add)}
                      icon={<PlusOutlined />}
                    />
                  </Form.Item>
                </>
              )}
            </Form.List>
            <span style={{ color: "red", fontSize: 11, textAlign: "center", display: "block" }}>
              Advanced settings. Set only if instructed by support team.
            </span>
            <Form.Item style={{ marginBottom: 0, marginTop: "1em" }} wrapperCol={{ span: 24 }}>
              <Space size={10} style={{ float: 'right' }}>
                <Button htmlType='button' onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button key='submit' type='primary' htmlType='submit' loading={isLoadingVariables}>
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Spin>
    </XmModal>
  );
};

export default AdvancedSettingsCFG;